import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DataGridPro, 
  GridColDef, 
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid-pro';
import { PlaylistListItem, PlaylistListItemDto } from '@/app2/shared/data';
import { usePlaylistsListGrid } from './hooks';
import { makeStyles } from 'tss-react/mui';
import useCustomDataGrid from '@/app2/shared/hooks/useCustomDataGrid';

const useStyles = makeStyles()((theme) => ({
  dataGrid: {
    // Container border and single/thin dividers come from adminTheme MuiDataGrid
    // (root border, cell borderTop). Do not override border or columnHeaders/cell borders.
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}));

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
      />
      <GridToolbarFilterButton {...({} as any)} />
      <GridToolbarExport
        placeholder={undefined}
        onPointerEnterCapture={undefined}
        onPointerLeaveCapture={undefined}
        onResize={undefined}
        onResizeCapture={undefined}
      />
    </GridToolbarContainer>
  );
}

export const PlaylistsListGrid: FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();

	const {
		const: {filterModel},
		api: {
      assignFilters,
			getPersistedFilterModel,
			handleFilterModelChange
		}} = useCustomDataGrid<PlaylistListItemDto>({ 
			defaultFilterSettings: {
        page: 1,
        pageSize: 25,
        sortDirection: 'desc',
      },
			filterStorageKey: "playlistFilterSettings" 
	});
  const initialFilterSettings = getPersistedFilterModel();
  
  const {
    rows,
    rowCount,
    isLoading,
    page,
    pageSize,
    sortModel,
    onPageChange,
    onPageSizeChange,
    onSortModelChange,
    onFilterChange,
    defaultInitialState,
    selectionModel,
    setSelectionModel,
  } = usePlaylistsListGrid({ onFilterModelChange: handleFilterModelChange, initialFilterSettings });

  const columns: GridColDef<PlaylistListItem>[] = [
    {
      field: 'title',
      headerName: 'Title',
      type: 'string',
      flex: 1,
      minWidth: 240,
      filterable: true,
    },
    {
      field: 'courseCount',
      headerName: 'Courses',
      type: 'number',
      width: 120,
      sortable: false,
      filterable: false,
    },
    {
      field: 'enrollmentCount',
      headerName: 'Enrolled',
      type: 'number',
      width: 120,
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <DataGridPro
      className={classes.dataGrid}
      rows={rows}
      columns={columns.map(e => assignFilters(e as any))}
      rowCount={rowCount}
      loading={isLoading}
      pagination
      paginationMode="server"
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      rowsPerPageOptions={[25, 50, 100, 200]}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      initialState={defaultInitialState}
      filterMode="server"
      filterModel={filterModel}
      onFilterModelChange={onFilterChange}
      checkboxSelection
      onSelectionModelChange={setSelectionModel}
      selectionModel={selectionModel}
      onRowClick={(params) => navigate(`/admin/playlists/${params.id}/enrollments`)}
      components={{
        Toolbar: CustomToolbar,
      }}
      autoHeight
    />
  );
};

export default PlaylistsListGrid;
